import { useEffect, useState } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo, milestones, testimonials } from '@/mocks/laceData';

export default function AboutPage() {
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = '关于我们 | 蕾丝雅集 Lace Elegance';
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormSuccess('感谢您的留言，我们会尽快与您联系。');
      form.reset();
      return;
    }
    formData.delete('company_alt');

    setSubmitting(true);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9so3jl8o1vn02ekuis0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }

      if (res.ok && parsed.code === 'OK') {
        setFormSuccess('感谢您的留言！我们会尽快与您联系。');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || text || '提交失败，请稍后再试。';
        setFormError(msg);
      }
    } catch {
      setFormError('网络错误，请稍后再试。');
    } finally {
      setSubmitting(false);
    }
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: window.location.origin + __BASE_PATH__,
    description: siteInfo.description,
    telephone: siteInfo.phone,
    email: siteInfo.email,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '南京西路1688号蕾丝大厦18层',
      addressLocality: '上海',
      addressRegion: '上海市',
      addressCountry: 'CN',
      postalCode: '200040',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: siteInfo.phone,
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: window.location.origin + __BASE_PATH__ + '/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />
      <div className="pt-[72px]">
        <div className="mx-auto px-4 md:px-6" style={{ maxWidth: '1280px' }}>
          <Breadcrumb items={[{ label: '关于我们' }]} />
        </div>

        <section className="relative w-full h-[350px] md:h-[420px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20lace%20fabric%20draped%20on%20a%20vintage%20wooden%20table%2C%20soft%20natural%20light%20from%20window%2C%20romantic%20atelier%20setting%2C%20delicate%20lace%20patterns%20in%20cream%20and%20ivory%2C%20luxury%20craftsmanship%20aesthetic%2C%20warm%20neutral%20tones%2C%20artistic%20editorial%20photography&width=1920&height=800&seq=about-hero-2026&orientation=landscape"
            alt="蕾丝雅集品牌"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/45"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <h1 className="font-heading text-2xl md:text-4xl font-semibold text-background-50 mb-3">
              关于蕾丝雅集
            </h1>
            <p className="text-background-200/90 text-sm md:text-base max-w-xl leading-relaxed">
              以匠心工艺演绎蕾丝之美，让经典优雅融入现代生活。
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6">
          <div className="mx-auto" style={{ maxWidth: '900px' }}>
            <div className="text-center mb-12">
              <p className="text-primary-500 text-sm tracking-[0.2em] uppercase mb-3 font-label">Brand Story</p>
              <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-6">
                品牌故事
              </h2>
              <div className="text-foreground-700 text-sm md:text-base leading-relaxed space-y-5 text-left">
                <p>
                  蕾丝雅集（Lace Elegance）创立于2015年，源于创始人对蕾丝艺术深深的热爱。品牌成立的初衷，是希望将欧洲传统蕾丝工艺与东方女性的审美需求相融合，打造既有国际视野又不失东方韵味的蕾丝生活方式品牌。
                </p>
                <p>
                  我们深知，真正的蕾丝之美不仅在于其精致的镂空工艺，更在于每一寸面料背后所蕴含的匠人精神。因此，从创立之初，蕾丝雅集就坚持与法国、意大利等欧洲顶级蕾丝工坊建立深度合作，直接从源头甄选最为优质的蕾丝面料。
                </p>
                <p>
                  在设计与制作环节，我们汇聚了来自米兰、巴黎和上海的资深设计师团队。他们将东西方美学理念融入蕾丝设计之中，创造出兼具经典韵味与现代气息的作品。每一件蕾丝单品，从设计草图到成品诞生，都经历了无数次的打磨与调整，只为呈现最为完美的效果。
                </p>
                <p>
                  十年来，蕾丝雅集始终践行"让每一件蕾丝成为经典"的品牌理念，用心打造涵盖服装、配饰、家居等多品类的蕾丝产品。我们相信，蕾丝不仅仅是一种面料，更是一种生活态度——它代表着对美的追求，对品质的坚持，以及对优雅生活方式的向往。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="mx-auto" style={{ maxWidth: '1280px' }}>
            <div className="text-center mb-12">
              <p className="text-accent-600 text-sm tracking-[0.2em] uppercase mb-3 font-label">Milestones</p>
              <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-4">
                品牌历程
              </h2>
            </div>
            <div className="relative">
              <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-background-300"></div>
              <div className="flex flex-col gap-8">
                {milestones.map((m, index) => (
                  <div
                    key={m.year}
                    className={`flex flex-col md:flex-row items-center gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70">
                        <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">
                          {m.title}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-accent-500 text-background-50 font-heading text-sm font-semibold flex-shrink-0">
                      {m.year}
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6">
          <div className="mx-auto" style={{ maxWidth: '1280px' }}>
            <div className="text-center mb-12">
              <p className="text-primary-500 text-sm tracking-[0.2em] uppercase mb-3 font-label">Testimonials</p>
              <h2 className="font-heading text-2xl md:text-4xl font-semibold text-foreground-900 mb-4">
                用户口碑
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
                来自时尚界专业人士和蕾丝爱好者的真实评价，是对我们最大的肯定。
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {testimonials.map((t) => (
                <div
                  key={t.name}
                  className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70"
                >
                  <div className="flex items-center gap-4 mb-5">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <h4 className="font-heading text-base font-semibold text-foreground-900">
                        {t.name}
                      </h4>
                      <p className="text-xs text-foreground-500">{t.role}</p>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed italic">
                    &ldquo;{t.content}&rdquo;
                  </p>
                  <div className="flex gap-1 mt-4 text-accent-500">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className="ri-star-fill text-xs"></i>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
          <div className="mx-auto" style={{ maxWidth: '1280px' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
              <div>
                <p className="text-primary-500 text-sm tracking-[0.2em] uppercase mb-3 font-label">Contact Us</p>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900 mb-6">
                  联系我们
                </h2>
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-map-pin-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-1">地址</p>
                      <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-phone-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-1">电话</p>
                      <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-mail-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-1">邮箱</p>
                      <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                      <i className="ri-wechat-line text-primary-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900 mb-1">微信公众号</p>
                      <p className="text-sm text-foreground-600">{siteInfo.wechat}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200/70">
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-6">
                    给我们留言
                  </h3>
                  {formSuccess ? (
                    <div className="bg-accent-100 border border-accent-300 rounded-md p-4">
                      <p className="text-sm text-accent-800 flex items-center gap-2">
                        <i className="ri-check-line"></i> {formSuccess}
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-readdy-form="">
                      <div>
                        <label htmlFor="about-name" className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                        <input
                          id="about-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          id="about-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md outline-none focus:border-primary-400 transition-colors duration-200"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-message" className="block text-sm text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="about-message"
                          name="message"
                          rows={4}
                          required
                          maxLength={500}
                          className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                          placeholder="请输入您想说的话（最多500字）"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="company_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="form-honeypot"
                      />
                      {formError && (
                        <div className="bg-primary-100 border border-primary-300 rounded-md p-3">
                          <p className="text-sm text-primary-700">{formError}</p>
                        </div>
                      )}
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-md transition-all duration-200 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                      >
                        {submitting ? '提交中...' : '发送留言'}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}